import { useState, useEffect } from 'react';
import { useProjects } from './useProjects';
import { listReviews } from '../api/reviews';
import { listTasks } from '../api/tasks';
import useAuth from './useAuth';

/**
 * useSearchData
 *
 * Fetches and normalizes data for global search across projects, reviews, and tasks.
 * Data is fetched on mount and filtered client-side.
 *
 * TODO: Add transmittals once backend API is available.
 */
export function useSearchData() {
  const { user, userProfile } = useAuth();
  const { projects } = useProjects(user);
  const [searchItems, setSearchItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSearchData = async () => {
      if (!userProfile?.id) {
        setSearchItems([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch reviews and tasks in parallel
        const [reviewsData, tasksData] = await Promise.all([
          listReviews().catch(() => []),
          listTasks().catch(() => []),
        ]);

        // Normalize projects
        const projectItems = (projects || []).map((project) => ({
          type: 'project',
          id: project.id,
          title: project.name || '',
          subtitle: project.code || project.client || '',
          route: `/projects/${project.id}`,
          searchableText: [
            project.name || '',
            project.code || '',
            project.client || '',
          ].join(' ').toLowerCase(),
        }));

        // Normalize reviews
        const reviewItems = (reviewsData || []).map((review) => ({
          type: 'review',
          id: review.id,
          title: review.linkedTask || review.title || '',
          subtitle: review.projectName || '',
          // Reviews render detail in-page, so deep-link via query param instead of /reviews/:id.
          route: `/reviews?reviewId=${encodeURIComponent(review.id)}`,
          searchableText: [
            review.linkedTask || '',
            review.projectName || '',
            review.designer || '',
            review.reviewer || '',
          ].join(' ').toLowerCase(),
        }));

        // Normalize tasks
        const taskItems = (tasksData || []).map((task) => ({
          type: 'task',
          id: task.id,
          title: task.title || '',
          subtitle: task.projectName || '',
          // Tasks render detail in-page, so deep-link via query param instead of /tasks/:id.
          route: `/tasks?taskId=${encodeURIComponent(task.id)}`,
          searchableText: [
            task.title || '',
            task.description || '',
            task.owners?.join(' ') || '',
          ].join(' ').toLowerCase(),
        }));

        // Combine all items
        const allItems = [...projectItems, ...reviewItems, ...taskItems];
        setSearchItems(allItems);
      } catch (error) {
        console.error('Error fetching search data:', error);
        setSearchItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchData();
  }, [userProfile, projects]);

  return { searchItems, loading };
}
